import React, { useContext, useState, useEffect } from 'react';
import { FBAInboundContext } from '../FBAInboundContext';
import { cmToMm, kgToGm } from '../utils/conversions';
import * as api from '../services/fbaInboundApi';
import { Package, CheckCircle, Loader2, AlertTriangle, Box, RefreshCw } from 'lucide-react';

const Step3_PackingConfirmation = () => {
  const {
    planId,
    plan,
    outboundId,
    outboundShipment,
    outboundItems,
    existingBoxContents,
    selectedPackingOption,
    nextStep,
    loading,
    refreshPlan,
    markStepCompleted,
    setPlanId,
    createPlan
  } = useContext(FBAInboundContext);

  const [step, setStep] = useState('review'); // 'review', 'confirming_packing', 'submitting', 'success', 'recovering'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);
  const [showRecoveryOption, setShowRecoveryOption] = useState(false);

  // Check if packing is already confirmed or packing info is set
  useEffect(() => {
    if (selectedPackingOption?.confirmed_at || plan?.is_packing_information_set) {
      setStep('success');
      // Mark step as completed so Next button works
      markStepCompleted(3);
    }
  }, [selectedPackingOption, plan?.is_packing_information_set, markStepCompleted]);

  // Calculate totals
  const totalBoxes = existingBoxContents.length;
  const totalItems = existingBoxContents.reduce((sum, box) =>
    sum + box.items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0), 0
  );
  const totalWeight = existingBoxContents.reduce((sum, box) => sum + (parseFloat(box.weight) || 0), 0);

  // Poll operation status until complete
  const pollOperationStatus = async (operationId) => {
    const maxAttempts = 30;
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const result = await api.checkOperationStatus(operationId);
        console.log('Operation status:', result);

        if (result.data?.status !== 'IN_PROGRESS') {
          return result.data;
        }

        // Wait before next poll (2s for first 30s, then 5s)
        const waitTime = attempts < 15 ? 2000 : 5000;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        attempts++;
      } catch (err) {
        console.error('Error polling operation:', err);
        throw err;
      }
    }

    throw new Error('Operation timed out');
  };

  // Check if error indicates a canceled/bad plan that needs recovery
  const isPlanBadStateError = (errorMessage) => {
    const badStateIndicators = [
      'cancelled inbound plan',
      'canceled inbound plan',
      'plan is in a bad state',
      'plan not found',
      'invalid plan'
    ];
    const lowerMessage = errorMessage.toLowerCase();
    return badStateIndicators.some(indicator => lowerMessage.includes(indicator));
  };

  // Auto-recover by creating a new plan and resubmitting packing
  const handleAutoRecover = async () => {
    setIsRecovering(true);
    setStep('recovering');
    setSubmitError(null);

    try {
      // Step 1: Clear the bad plan
      setStatusMessage('Clearing old plan...');
      if (planId) {
        try {
          await api.deletePlan(planId);
        } catch (delErr) {
          console.warn('Could not delete old plan (may already be canceled):', delErr);
        }
      }
      setPlanId(null);

      // Step 2: Prepare items from outbound
      setStatusMessage('Preparing items...');
      const items = outboundItems.map(item => ({
        msku: item.item?.merchant_sku || item.merchant_sku || '',
        quantity: item.quantity || 1,
        expiration: null,
        label_owner: 'SELLER',
        prep_owner: 'SELLER'
      }));

      if (items.length === 0) {
        throw new Error('No items found in outbound shipment');
      }

      // Step 3: Create new plan
      setStatusMessage('Creating new plan...');
      const channelId = outboundShipment?.channel_id || 126;
      await createPlan(channelId, items);

      // Step 4: Now submit box contents (the createPlan function already waits for packing options)
      setStatusMessage('Recovery complete! Submitting box contents...');

      // Small delay to let state update
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Reset to review state and let user click submit again
      setStep('review');
      setStatusMessage('');
      setSubmitError(null);
      setShowRecoveryOption(false);

    } catch (err) {
      console.error('Auto-recovery failed:', err);
      setSubmitError(`Recovery failed: ${err.message}. Please try manually deleting the plan and starting over.`);
      setStep('review');
    } finally {
      setIsRecovering(false);
    }
  };

  const handleSubmitBoxContents = async () => {
    if (existingBoxContents.length === 0) {
      setSubmitError('No box contents found. Please configure boxes first.');
      return;
    }

    // Validate all boxes have required dimensions and weight
    const invalidBoxes = existingBoxContents.filter(box => {
      const length = parseFloat(box.length) || 0;
      const width = parseFloat(box.width) || 0;
      const height = parseFloat(box.height) || 0;
      const weight = parseFloat(box.weight) || 0;
      return length <= 0 || width <= 0 || height <= 0 || weight <= 0;
    });

    if (invalidBoxes.length > 0) {
      const boxNumbers = invalidBoxes.map(b => `#${b.boxNumber}`).join(', ');
      setSubmitError(`Box(es) ${boxNumbers} are missing dimensions or weight. Please update box configuration first.`);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const API_URL = import.meta.env.VITE_API_URL;
      const planItems = plan?.items || [];

      // Check if packing is already complete - skip ahead
      if (plan?.is_packing_information_set === true) {
        console.log('Packing information already set, skipping to success');
        setStep('success');
        setTimeout(() => nextStep(), 1500);
        return;
      }

      // STEP 1: Confirm packing option first (if only 1, auto-confirm)
      setStep('confirming_packing');
      setStatusMessage('Checking packing option status...');

      const packingOptions = plan?.packing_options || [];
      const packingOption = packingOptions.find(opt => opt.status === 'ACCEPTED') || packingOptions[0];

      // Use packing_option_id (Amazon's ID starting with 'po'), NOT the internal id
      const packingOptionId = packingOption?.packing_option_id;
      if (!packingOptionId) {
        throw new Error('No packing option found in plan');
      }

      console.log('Packing option:', packingOption);
      console.log('Using packing_option_id:', packingOptionId);

      // Check if already confirmed
      const isAlreadyConfirmed = packingOption.confirmed_at || packingOption.status === 'CONFIRMED';

      if (!isAlreadyConfirmed) {
        setStatusMessage('Confirming packing option...');
        console.log('Confirming packing option:', packingOptionId);

        let confirmationSucceeded = false;

        try {
          // Call confirm-packing-option with the Amazon packing_option_id
          const confirmResult = await api.confirmPackingOption(planId, packingOptionId);
          console.log('Confirm packing option result:', confirmResult);

          // The response is an operation ID (string)
          const operationId = typeof confirmResult === 'string' ? confirmResult : confirmResult.data?.id || confirmResult.operation_id;

          if (operationId) {
            setStatusMessage('Waiting for Amazon confirmation...');
            const operationResult = await pollOperationStatus(operationId);

            if (operationResult?.status === 'ERROR') {
              console.warn('Operation had errors:', operationResult.problems);
              // Check if it was already confirmed
            } else if (operationResult?.status === 'SUCCESS') {
              confirmationSucceeded = true;
            }
          } else {
            // No operation ID means it was instant success
            confirmationSucceeded = true;
          }

          // Mark packing option as confirmed
          setStatusMessage('Marking packing option as confirmed...');
          await api.markPackingOptionAsConfirmed(planId, packingOptionId);
          console.log('Packing option marked as confirmed');
          confirmationSucceeded = true;
        } catch (confirmErr) {
          console.warn('Confirm packing option failed:', confirmErr.message);

          // Refresh plan to check if it's actually confirmed now
          setStatusMessage('Checking confirmation status...');
          await refreshPlan();

          // Re-fetch the plan to see current state
          const refreshedPlan = await api.getPlan(planId);
          const refreshedPackingOptions = refreshedPlan?.data?.packing_options || [];
          const refreshedOption = refreshedPackingOptions.find(opt => opt.packing_option_id === packingOptionId);

          if (refreshedOption?.confirmed_at || refreshedOption?.status === 'CONFIRMED') {
            console.log('Packing option was already confirmed');
            confirmationSucceeded = true;
          } else {
            // Plan may be corrupted - show error to user
            throw new Error(`Could not confirm packing option. This plan may be in a bad state. Error: ${confirmErr.message}. Try deleting this plan and creating a new one.`);
          }
        }

        if (!confirmationSucceeded) {
          throw new Error('Packing option confirmation failed. Please try deleting this plan and creating a new one.');
        }
      } else {
        console.log('Packing option already confirmed, skipping confirmation step');
      }

      // STEP 2: Now submit box contents
      setStep('submitting');
      setStatusMessage('Uploading box contents...');

      // Get packing group ID
      const packingGroupId = packingOption.packing_groups?.[0]?.id;
      if (!packingGroupId) {
        throw new Error('No packing group found in packing option');
      }

      console.log('Using packing group ID:', packingGroupId);

      // Delete existing box groups if any
      if (plan?.box_groups && plan.box_groups.length > 0) {
        setStatusMessage('Clearing existing box groups...');
        for (const boxGroup of plan.box_groups) {
          try {
            await fetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups/${boxGroup.id}`, {
              method: 'DELETE'
            });
          } catch (err) {
            console.log('Error deleting old box group:', err);
          }
        }
      }

      // Create box groups from existing box contents
      for (let i = 0; i < existingBoxContents.length; i++) {
        const box = existingBoxContents[i];
        setStatusMessage(`Creating box ${i + 1} of ${existingBoxContents.length}...`);

        // Create box group with dimensions
        const boxGroupData = {
          length_mm: Math.max(1, cmToMm(parseFloat(box.length) || 0)),
          width_mm: Math.max(1, cmToMm(parseFloat(box.width) || 0)),
          height_mm: Math.max(1, cmToMm(parseFloat(box.height) || 0)),
          weight_gm: Math.max(1, kgToGm(parseFloat(box.weight) || 0)),
          associated_entity_id: packingGroupId,
          version: "v2024"
        };

        console.log('Creating box group:', boxGroupData);

        const createResponse = await fetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(boxGroupData)
        });

        if (!createResponse.ok) {
          const errData = await createResponse.json();
          throw new Error(`Failed to create box group for box ${box.boxNumber}: ${errData.error || errData.message}`);
        }

        const boxGroupResult = await createResponse.json();
        const boxGroupId = boxGroupResult.data?.id || boxGroupResult.box_group_id;

        if (boxGroupId && box.items.length > 0) {
          // Add items to box group ONE AT A TIME
          for (const item of box.items) {
            const planItem = planItems.find(pi =>
              pi.msku === item.sku ||
              pi.merchant_sku === item.sku ||
              pi.fnsku === item.fnsku ||
              pi.msku === item.fnsku
            );

            if (!planItem?.id) {
              console.warn(`No plan_item_id found for item ${item.sku || item.fnsku}, skipping`);
              continue;
            }

            const itemData = {
              plan_item_id: planItem.id,
              quantity: item.quantity || 1,
              expiration: null,
              version: "v2024"
            };

            await fetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups/${boxGroupId}/items`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(itemData)
            });
          }

          // Set box quantity to 1
          await fetch(`${API_URL}/api/fba-inbound/plans/${planId}/box-groups/${boxGroupId}/set-box-quantity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: 1 })
          });
        }
      }

      // STEP 3: Submit packing information
      setStatusMessage('Finalizing packing information...');
      try {
        const setPackingResult = await api.setPackingInformation(planId);
        console.log('Set packing information result:', setPackingResult);

        // This returns an operation ID, poll it
        const setPackingOpId = typeof setPackingResult === 'string' ? setPackingResult : setPackingResult.data?.id;
        if (setPackingOpId) {
          setStatusMessage('Waiting for packing information to be processed...');
          await pollOperationStatus(setPackingOpId);
        }
      } catch (packingErr) {
        console.warn('set-packing-information returned error (may be expected):', packingErr.message);
      }

      // STEP 4: Add carton request to submission - this finalizes the packing
      setStatusMessage('Submitting carton information...');
      try {
        // Get all packing group IDs from the packing option
        const packingGroupIds = packingOption.packing_groups?.map(pg => pg.id) || [packingGroupId];
        console.log('Adding carton request with packing group IDs:', packingGroupIds);

        const cartonResult = await api.addCartonRequestToSubmission(planId, packingGroupIds);
        console.log('Add carton request result:', cartonResult);
      } catch (cartonErr) {
        console.warn('add-carton-request-to-submission error:', cartonErr.message);
      }

      // Verify packing information was set by polling the plan
      setStatusMessage('Verifying packing information...');
      let packingInfoSet = false;
      let verifyRetries = 0;
      const maxVerifyRetries = 10;

      while (!packingInfoSet && verifyRetries < maxVerifyRetries) {
        const verifyPlan = await api.getPlan(planId);
        console.log(`Verify attempt ${verifyRetries + 1}, is_packing_information_set:`, verifyPlan.data?.is_packing_information_set);

        if (verifyPlan.data?.is_packing_information_set === true) {
          packingInfoSet = true;
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
        verifyRetries++;
      }

      if (!packingInfoSet) {
        throw new Error('Packing information was not confirmed by Amazon. Please try again.');
      }

      // Refresh plan and move to success
      await refreshPlan();
      setStep('success');

      // Auto advance after short delay
      setTimeout(() => {
        nextStep();
      }, 1500);

    } catch (err) {
      console.error('Failed to submit box contents:', err);
      setSubmitError(err.message);
      setStep('review');

      // Check if this is a bad plan state error that can be auto-recovered
      if (isPlanBadStateError(err.message)) {
        setShowRecoveryOption(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (step === 'success') {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Packing Confirmed
        </h3>
        <p className="text-gray-600">
          Your box contents have been submitted to Amazon.
        </p>
      </div>
    );
  }

  // Recovering state
  if (step === 'recovering') {
    return (
      <div className="text-center py-12">
        <RefreshCw className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Auto-Recovering Plan
        </h3>
        <p className="text-gray-600">
          {statusMessage || 'Creating new plan and restoring progress...'}
        </p>
        <div className="mt-4 max-w-md mx-auto bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-700">
            The previous plan was in a bad state. We're automatically creating a new one with your existing configuration.
          </p>
        </div>
      </div>
    );
  }

  // Submitting/confirming state
  if (step === 'confirming_packing' || step === 'submitting') {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {step === 'confirming_packing' ? 'Confirming Packing Option' : 'Uploading Box Contents'}
        </h3>
        <p className="text-gray-600">
          {statusMessage}
        </p>
      </div>
    );
  }

  // Review existing boxes step
  return (
    <div className="space-y-6">
      {/* Info Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Package className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-900">Review Box Contents</h4>
            <p className="text-sm text-blue-700 mt-1">
              The following boxes will be submitted to Amazon. These are pulled from your existing box configuration.
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-red-900">Error</h4>
              <p className="text-sm text-red-700 mt-1">{submitError}</p>

              {/* Auto-Recovery Button */}
              {showRecoveryOption && (
                <div className="mt-4 pt-4 border-t border-red-200">
                  <p className="text-sm text-red-800 mb-3">
                    This plan appears to be in a bad state (possibly canceled by Amazon).
                    Click below to automatically create a new plan and restore your progress.
                  </p>
                  <button
                    onClick={handleAutoRecover}
                    disabled={isRecovering}
                    className="inline-flex items-center px-4 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50"
                  >
                    {isRecovering ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Recovering...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Auto-Recover Plan
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {existingBoxContents.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{totalBoxes}</div>
            <div className="text-sm text-gray-500">Boxes</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{totalItems}</div>
            <div className="text-sm text-gray-500">Total Items</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{totalWeight.toFixed(1)}</div>
            <div className="text-sm text-gray-500">Total kg</div>
          </div>
        </div>
      )}

      {/* Box List */}
      {existingBoxContents.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Boxes ({totalBoxes})</h3>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {existingBoxContents.map((box) => (
              <div key={box.boxNumber} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Box className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        Box #{box.boxNumber}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {box.length} x {box.width} x {box.height} cm - {box.weight} kg
                      </div>
                      <div className="text-sm text-gray-500">
                        {box.items.length} item(s) - {box.items.reduce((sum, i) => sum + (i.quantity || 0), 0)} units
                      </div>
                    </div>
                  </div>
                  {box.pallet && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      Pallet {box.pallet}
                    </span>
                  )}
                </div>

                {/* Items Preview */}
                {box.items.length > 0 && (
                  <div className="mt-3 pl-12">
                    <div className="text-xs text-gray-500 space-y-1">
                      {box.items.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="truncate max-w-xs">
                            {item.sku || item.fnsku} - {item.title?.substring(0, 40) || 'No title'}
                          </span>
                          <span className="text-gray-400 ml-2">x{item.quantity}</span>
                        </div>
                      ))}
                      {box.items.length > 3 && (
                        <div className="text-gray-400">
                          +{box.items.length - 3} more items
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-900 font-medium">No Box Contents Found</p>
          <p className="text-sm text-gray-500 mt-1">
            Please configure box contents for this outbound shipment first.
          </p>
          <a
            href={`/outbound/${outboundId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center mt-4 text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            Open Outbound to Configure Boxes
          </a>
        </div>
      )}

      {/* Submit Button */}
      {existingBoxContents.length > 0 && (
        <div className="pt-4">
          <button
            onClick={handleSubmitBoxContents}
            disabled={isSubmitting || loading}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Uploading to Amazon...
              </>
            ) : (
              <>
                <Package className="w-5 h-5 mr-2" />
                Submit {totalBoxes} Box(es) to Amazon
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step3_PackingConfirmation;
